import { applyHubAccessHeader } from '@/api/hubAccessHeaders';
import { HUB_API_URL } from '@/config/env';

// The hub gates POST/GET/PATCH /api/v1/org-tokens (and the hospital/agent
// admin routes) behind BACKOFFICE_TOKEN — a shared secret, not a real
// operator login (see byo-health-hub's orgToken.routes.js). POST
// /api/v1/backoffice/login exchanges it, once, for an httpOnly cookie the
// hub sets and every later request carries automatically (see hubApi.ts's
// `credentials: 'include'`) — this page's own JS never holds or sends the
// raw secret again, unlike the old sessionStorage + Authorization header.
const CONNECTED_FLAG_KEY = 'byo-backoffice-connected';

// Not the credential — the httpOnly cookie is unreadable from here by
// design. Just a UI hint for whether the dialog should read "connected"
// across a reload, in the same browser tab (sessionStorage, not
// localStorage, so it clears when the tab closes, same as the cookie's own
// practical lifetime for this use).
function setConnectedFlag(connected: boolean): void {
    try {
        if (connected) sessionStorage.setItem(CONNECTED_FLAG_KEY, '1');
        else sessionStorage.removeItem(CONNECTED_FLAG_KEY);
    } catch {
        // Private browsing / blocked storage: the hint just won't persist
        // across reloads, which is a worse UX, not a broken one.
    }
}

export function isBackofficeConnected(): boolean {
    try {
        return sessionStorage.getItem(CONNECTED_FLAG_KEY) === '1';
    } catch {
        return false;
    }
}

// The connected flag is only ever set from a login response — it can't
// detect the cookie going bad afterwards (expiry, a hub restart, or, as
// found 2026-10-12, a hub where BACKOFFICE_TOKEN isn't configured: its login
// route treats that as "open" and returns 204, but every protected route
// still fails closed, so the UI looks connected while every real request
// 401s). hubApi's baseQuery calls this on any 401 it sees, and App listens
// to drop back to the login page instead of leaving a dead dashboard up.
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onBackofficeUnauthorized(listener: UnauthorizedListener): () => void {
    unauthorizedListeners.add(listener);
    return () => unauthorizedListeners.delete(listener);
}

export function markBackofficeUnauthorized(): void {
    setConnectedFlag(false);
    for (const listener of unauthorizedListeners) listener();
}

export async function loginBackoffice(token: string): Promise<boolean> {
    const response = await fetch(`${HUB_API_URL}/api/v1/backoffice/login`, {
        method: 'POST',
        credentials: 'include',
        headers: applyHubAccessHeader(new Headers({ 'content-type': 'application/json' })),
        body: JSON.stringify({ token }),
    });
    setConnectedFlag(response.ok);
    return response.ok;
}

export async function logoutBackoffice(): Promise<void> {
    try {
        await fetch(`${HUB_API_URL}/api/v1/backoffice/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: applyHubAccessHeader(new Headers()),
        });
    } finally {
        setConnectedFlag(false);
    }
}
